import { Alert, Stack } from '@mui/material';
import { t } from 'i18next';
import { FormEventHandler, useEffect, useState } from 'react';
import { Dialog, DialogBody } from '../../common/components/Modal/Modal';
import { getErrorMessage } from '../../common/errors/errors';
import { useFormItem } from '../../common/hooks/use-form-item';
import { InputType } from '../../common/hooks/use-form-item/types';
import { Interpolation } from '../../config/i18n/components/Interpolation';
import { ButtonBox, DangerButton, TertiaryButton } from '../components/Buttons';
import { ProjectService } from '../services/ProjectService';

type Props = {
  projectId: number;
  projectName: string;
  onClosed: (projectId: number | undefined) => void;
};

export const DeleteProjectFlow = ({ projectId, projectName, onClosed }: Props) => {
  // It's not a preference to have services in states, but we use it here so
  // that we can leverage same service in multiple functionalities.
  const [projectService] = useState<ProjectService>(new ProjectService());
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      projectService.cancelAllRequests();
    };
  }, [projectService]);

  const [name, nameComponent] = useFormItem({
    type: InputType.TYPE_TEXT,
    label: t('deleteProject.name.input'),
    value: '',
    validator: null,
    inputOptions: {
      autoFocus: true,
      disabled: loading,
      focusedLabel: t('deleteProject.name.focused'),
      showRequiredIndicator: true,
    },
  });

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      await projectService.deleteProject(projectId);
      setLoading(false);
      onClosed(projectId);
    } catch (e: unknown) {
      setError(getErrorMessage(e));
      setLoading(false);
    }
  };

  // Using a form validator results in the input being marked as invalid
  // while the user is typing. We manually check if the typed value equals
  // the project name to enable the submit button
  const canSubmit = !loading && name === projectName;

  return (
    <Dialog title={t('deleteProject.title')} onDismiss={() => onClosed(undefined)}>
      <DialogBody>
        <Stack spacing={1.5} component="form" onSubmit={onSubmit}>
          {error && <Alert severity="error">{error}</Alert>}
          <Interpolation i18nKey="deleteProject.content" items={[{ name: 'color_gray', color: 'grey.700' }]} values={{ projectName }} />
          {nameComponent}
          <ButtonBox>
            <TertiaryButton disabled={loading} onClick={() => onClosed(undefined)}>
              {t('deleteProject.back')}
            </TertiaryButton>
            <DangerButton disabled={!canSubmit} type="submit">
              {t('deleteProject.submit')}
            </DangerButton>
          </ButtonBox>
        </Stack>
      </DialogBody>
    </Dialog>
  );
};
